import type { 
  GrammarError, 
  CorrectionSuggestion, 
  ErrorLevel 
} from '$lib/types/grammar';

/**
 * Factory service for creating unified GrammarError objects
 * Handles the creation of errors from both regular grammar check and AI analysis
 */
export class GrammarErrorFactory {
  /**
   * Create GrammarError from Bareun API response
   * @param apiError Raw error from Bareun API
   * @param index Error index for ID generation
   * @returns Unified GrammarError object
   */
  static fromBareunApiError(apiError: any, index: number): GrammarError {
    // Create structured corrections from candWord array
    const corrections: CorrectionSuggestion[] = (apiError.candWord || []).map((word: string, idx: number) => ({
      id: `correction-${index}-${idx}`,
      text: word,
      description: apiError.help || '수정 제안'
    }));

    return {
      id: `error-${Date.now()}-${index}`,
      type: this.mapBareunErrorType(apiError.errorCode),
      level: this.mapBareunErrorLevel(apiError.errorCode),
      position: {
        start: apiError.startPos,
        end: apiError.endPos
      },
      original: apiError.orgStr,
      corrections,
      message: apiError.message,
      help: apiError.help,
      confidence: 0.9, // Bareun API errors are generally high confidence
      
      // Legacy compatibility fields
      suggestions: apiError.candWord || [],
      originalText: apiError.orgStr
    };
  }

  /**
   * Create GrammarError from AI analysis result
   * @param aiError Raw error from AI analysis
   * @param index Error index for ID generation
   * @param fullText Full text to search in
   * @returns Unified GrammarError object
   */
  static fromAIAnalysisError(aiError: any, index: number, fullText: string): GrammarError {
    // Use timestamp + index + random to ensure unique IDs even in same millisecond
    const errorId = `ai-${Date.now()}-${index}-${Math.random().toString(36).substr(2, 9)}`;
    
    // Find the actual position in text - search entire text first, not based on approximate position
    const exactPosition = fullText.indexOf(aiError.original);
    const position = exactPosition !== -1 
      ? { start: exactPosition, end: exactPosition + aiError.original.length }
      : this.findTextPosition(fullText, aiError.original, aiError.position || 0);
    
    
    // Create structured corrections
    const corrections: CorrectionSuggestion[] = [{
      id: `${errorId}-correction`,
      text: aiError.correction,
      description: aiError.explanation,
      confidence: aiError.confidence || 0.8
    }];

    return {
      id: errorId,
      type: aiError.type || 'grammar',
      level: 'error',
      position,
      original: aiError.original,
      corrections,
      isAIGenerated: true,  // Mark as AI-generated to avoid duplicate display
      message: aiError.explanation || 'AI가 발견한 오류',
      help: undefined,  // Don't set help to avoid duplicate display
      confidence: aiError.confidence || 0.8,
      isAIOnly: true,
      aiComment: {
        text: aiError.explanation || 'AI가 발견한 오류',
        confidence: aiError.confidence || 0.8,
        agreesWithCorrection: true,
        timestamp: Date.now()
      },
      
      // Legacy compatibility fields
      suggestions: [aiError.correction],
      originalText: aiError.original
    };
  }

  /**
   * Create GrammarError from mixed sources (for migration/compatibility)
   * @param params Mixed parameters from various sources
   * @returns Unified GrammarError object
   */
  static createUnified(params: {
    id?: string;
    type?: 'spelling' | 'grammar' | 'punctuation' | 'style' | 'typo';
    level?: ErrorLevel;
    
    // Position - flexible input
    position?: { start: number; end: number };
    startPos?: number;
    endPos?: number;
    
    // Original text - flexible input
    original?: string;
    originalText?: string;
    orgStr?: string;
    
    // Corrections - flexible input
    corrections?: CorrectionSuggestion[];
    suggestions?: string[];
    candWord?: string[];
    
    // Messages
    message?: string;
    help?: string;
    explanation?: string;
    
    // Other fields
    confidence?: number;
    isAIOnly?: boolean;
  }): GrammarError {
    // Determine position
    const position = params.position || {
      start: params.startPos || 0,
      end: params.endPos || 0
    };

    // Determine original text
    const original = params.original || params.originalText || params.orgStr || '';

    // Create corrections array
    let corrections: CorrectionSuggestion[];
    if (params.corrections) {
      corrections = params.corrections;
    } else {
      const suggestionList = params.suggestions || params.candWord || [];
      corrections = suggestionList.map((text, idx) => ({
        id: `${params.id || 'unified'}-correction-${idx}`,
        text,
        description: params.help || params.explanation || '수정 제안'
      }));
    }

    return {
      id: params.id || `unified-${Date.now()}`,
      type: params.type || 'grammar',
      level: params.level || 'error',
      position,
      original,
      corrections,
      message: params.message || params.explanation || '오류가 발견되었습니다',
      help: params.help || params.explanation,
      confidence: params.confidence,
      isAIOnly: params.isAIOnly,
      
      // Legacy compatibility fields
      suggestions: corrections.map(c => c.text),
      originalText: original
    };
  }

  /**
   * Update an existing GrammarError to ensure it has all required fields
   * @param error Existing GrammarError that might be missing some fields
   * @returns Updated GrammarError with all unified fields
   */
  static normalizeExisting(error: Partial<GrammarError>): GrammarError {
    // Ensure corrections array exists and is properly structured
    let corrections: CorrectionSuggestion[];
    if (error.corrections && Array.isArray(error.corrections)) {
      // Ensure each correction has proper structure
      corrections = error.corrections.map((c, idx) => ({
        id: c.id || `${error.id}-correction-${idx}`,
        text: c.text,
        description: c.description,
        confidence: c.confidence
      }));
    } else if (error.suggestions && Array.isArray(error.suggestions)) {
      // Convert legacy suggestions to corrections
      corrections = error.suggestions.map((text, idx) => ({
        id: `${error.id}-correction-${idx}`,
        text,
        description: error.help || '수정 제안'
      }));
    } else {
      corrections = [];
    }

    return {
      id: error.id || `normalized-${Date.now()}`,
      type: error.type || 'grammar',
      level: error.level || 'error',
      position: error.position || { start: 0, end: 0 },
      original: error.original || error.originalText || '',
      corrections,
      message: error.message || '오류가 발견되었습니다',
      help: error.help,
      rule: error.rule,
      category: error.category,
      confidence: error.confidence,
      isResolved: error.isResolved,
      userAction: error.userAction,
      aiComment: error.aiComment,
      isAIOnly: error.isAIOnly,
      isAIGenerated: error.isAIGenerated,  // Preserve isAIGenerated flag
      isOriginalKept: error.isOriginalKept,
      
      // Legacy compatibility fields
      suggestions: corrections.map(c => c.text),
      originalText: error.original || error.originalText || ''
    };
  }

  /**
   * Helper method to find text position in the full text
   * Used as fallback when exact match is not found
   */
  private static findTextPosition(text: string, searchText: string, approximatePosition: number): { start: number, end: number } {
    // First, always try exact match in entire text
    const exactIndex = text.indexOf(searchText);
    if (exactIndex !== -1) {
      return { start: exactIndex, end: exactIndex + searchText.length };
    }
    
    // If not found, try near the approximate position
    if (approximatePosition >= 0) {
      const searchRadius = 100;
      const searchStart = Math.max(0, approximatePosition - searchRadius);
      const searchEnd = Math.min(text.length, approximatePosition + searchRadius + searchText.length);
      const searchArea = text.substring(searchStart, searchEnd);
      
      const relativeIndex = searchArea.indexOf(searchText);
      if (relativeIndex !== -1) {
        const start = searchStart + relativeIndex;
        return { start, end: start + searchText.length };
      }
    }
    
    // Not found - return invalid position
    return { start: -1, end: -1 };
  }

  /**
   * Map Bareun API error code to our error type
   */
  private static mapBareunErrorType(errorCode: string | undefined): 'spelling' | 'grammar' | 'punctuation' | 'style' | 'typo' {
    if (!errorCode) return 'grammar';
    if (errorCode.includes('SPELLING')) return 'spelling';
    if (errorCode.includes('SPACING')) return 'punctuation';
    if (errorCode.includes('STYLE')) return 'style';
    return 'grammar';
  }

  /**
   * Map Bareun API error code to our error level
   */
  private static mapBareunErrorLevel(errorCode: string | undefined): ErrorLevel {
    if (!errorCode) return 'error';
    if (errorCode.includes('SUGGESTION')) return 'suggestion';
    if (errorCode.includes('WARNING')) return 'warning';
    return 'error';
  }

  /**
   * Get all suggestions from a GrammarError (both new and legacy format)
   */
  static getAllSuggestions(error: GrammarError): string[] {
    if (error.corrections && error.corrections.length > 0) {
      return error.corrections.map(c => c.text);
    }
    return error.suggestions || [];
  }

  /**
   * Get the original text from a GrammarError (both new and legacy format)
   */
  static getOriginalText(error: GrammarError): string {
    return error.original || error.originalText || '';
  }

  /**
   * Check if error is from AI analysis
   */
  static isAIError(error: GrammarError): boolean {
    return error.isAIOnly === true;
  }

  /**
   * Check if error has been resolved by user
   */
  static isResolved(error: GrammarError): boolean {
    return error.isResolved === true || 
           error.userAction === 'accepted' || 
           error.userAction === 'edited';
  }
}